import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Mail, MessageSquare } from "lucide-react";
import api from "@/lib/api";

const PHONE_RE = /^\+[1-9]\d{6,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function CityRequestModal({ onClose }) {
  const [method, setMethod] = useState("email"); // "email" | "sms"
  const [form, setForm] = useState({
    city: "",
    name: "",
    email: "",
    phone: "",
    notify_when_open: false,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const firstFieldRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const compactPhone = (v) => v.replace(/[\s\-().]/g, "");

  const validate = () => {
    const next = {};
    if (!form.city.trim()) next.city = "City is required.";
    if (method === "email") {
      const email = form.email.trim();
      if (!email) next.email = "Email is required.";
      else if (!EMAIL_RE.test(email)) next.email = "Enter a valid email.";
    } else {
      const phone = compactPhone(form.phone.trim());
      if (!phone) next.phone = "Phone number is required.";
      else if (!PHONE_RE.test(phone)) next.phone = "Use international format, e.g. +14155551234.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const payload = {
      city: form.city.trim(),
      name: form.name.trim() || undefined,
      contact_method: method,
      notify_when_open: !!form.notify_when_open,
    };
    if (method === "email") payload.email = form.email.trim();
    else payload.phone = compactPhone(form.phone.trim());
    try {
      await api.post("/city-requests", payload);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? (detail[0]?.msg || "Could not save.") : (detail || "Could not save.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const backdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      data-testid="city-request-modal"
      onMouseDown={backdropClick}
      className="fixed inset-0 z-[55] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="city-request-title"
        className="w-full max-w-md rounded-2xl border border-[color:var(--rc-border)] bg-[color:var(--rc-surface-1)] shadow-2xl overflow-hidden fade-up"
        style={{ maxHeight: "min(92vh, 760px)" }}
      >
        <div className="flex items-start justify-between px-5 sm:px-6 pt-5 pb-3 border-b border-[color:var(--rc-divider)]">
          <div className="pr-6">
            <div className="rc-eyebrow" style={{ fontSize: "0.68rem" }}>Request Your City</div>
            <h2 id="city-request-title" className="font-display text-xl sm:text-2xl font-semibold mt-1" style={{ letterSpacing: "-0.015em" }}>
              Where should Rebel go next?
            </h2>
          </div>
          <button
            data-testid="city-request-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[color:var(--rc-text-muted)] hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 sm:px-6 py-5" style={{ maxHeight: "calc(min(92vh, 760px) - 92px)" }}>
          {done ? (
            <div data-testid="city-request-success" className="text-center py-6">
              <div className="font-display text-xl sm:text-2xl font-semibold">Consider it noted.</div>
              <p className="mt-3 text-sm text-[color:var(--rc-text-muted)]">Rebel&amp;Co might be closer than you think.</p>
              <button
                data-testid="city-request-success-close"
                type="button"
                onClick={onClose}
                className="mt-6 rc-btn rc-btn-outline-new"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <p className="text-sm text-[color:var(--rc-text-muted)] leading-relaxed">
                Suggest a city and choose how to be contacted. No payment, no commitment.
              </p>

              <Field label="City" htmlFor="cr-city" error={errors.city}>
                <input
                  id="cr-city"
                  ref={firstFieldRef}
                  data-testid="city-request-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Toronto"
                  className="rc-field"
                  autoComplete="address-level2"
                />
              </Field>

              <Field label="Alias (optional)" htmlFor="cr-name">
                <input
                  id="cr-name"
                  data-testid="city-request-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rc-field"
                  autoComplete="nickname"
                />
              </Field>

              <div>
                <div className="text-sm font-medium text-[color:var(--rc-text)] mb-2">Reach me by</div>
                <div
                  role="radiogroup"
                  aria-label="Contact method"
                  className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-[color:var(--rc-surface-2)] border border-[color:var(--rc-divider)]"
                >
                  <MethodPill
                    testid="city-request-method-email"
                    active={method === "email"}
                    onClick={() => { setMethod("email"); setErrors({ ...errors, email: undefined, phone: undefined }); }}
                    icon={<Mail className="w-4 h-4" />}
                    label="Email"
                  />
                  <MethodPill
                    testid="city-request-method-sms"
                    active={method === "sms"}
                    onClick={() => { setMethod("sms"); setErrors({ ...errors, email: undefined, phone: undefined }); }}
                    icon={<MessageSquare className="w-4 h-4" />}
                    label="Text"
                  />
                </div>
              </div>

              {method === "email" ? (
                <Field label="Email" htmlFor="cr-email" error={errors.email}>
                  <input
                    id="cr-email"
                    data-testid="city-request-email"
                    type="email"
                    inputMode="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="rc-field"
                    autoComplete="email"
                  />
                </Field>
              ) : (
                <Field label="Phone" htmlFor="cr-phone" hint="International format, e.g. +14155551234." error={errors.phone}>
                  <input
                    id="cr-phone"
                    data-testid="city-request-phone"
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+14155551234"
                    className="rc-field"
                    autoComplete="tel"
                  />
                </Field>
              )}

              <label className="flex items-start gap-3 text-sm text-[color:var(--rc-text-muted)] cursor-pointer select-none pt-1">
                <input
                  data-testid="city-request-notify"
                  type="checkbox"
                  checked={form.notify_when_open}
                  onChange={(e) => setForm({ ...form, notify_when_open: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded accent-[#2d7cf6]"
                />
                <span>Let me know when bookings open in this city.</span>
              </label>

              <div className="pt-2">
                <button
                  data-testid="city-request-submit"
                  type="submit"
                  disabled={saving}
                  className="rc-btn rc-btn-blue w-full disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ minHeight: 44 }}
                >
                  {saving ? "Sending…" : "Request Your City"}
                </button>
                <p className="mt-3 text-[13px] text-[color:var(--rc-text-muted)] leading-relaxed">
                  Email or text — one required.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, hint, error, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-[color:var(--rc-text)] mb-1.5">{label}</label>
      {children}
      {hint && !error && (
        <p className="mt-1.5 text-[13px] text-[color:var(--rc-text-muted)]">{hint}</p>
      )}
      {error && (
        <p data-testid={`${htmlFor}-error`} className="mt-1.5 text-[13px] text-[#ff8a9a]">{error}</p>
      )}
    </div>
  );
}

function MethodPill({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-[#2d7cf6] text-white shadow-[0_4px_20px_rgba(45,124,246,0.25)]"
          : "text-[color:var(--rc-text-muted)] hover:text-[color:var(--rc-text)]"
      }`}
      style={{ minHeight: 40 }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
